import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { Options as BlotFormatterOptions } from "@enzedonline/quill-blot-formatter2";
import { TranslocoDirective } from "@jsverse/transloco";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { EditorModule } from "primeng/editor";
import { Subscription } from "rxjs";
import { langCodes } from "../../../../../domain/languages";

interface LangFormControls {
  fi: FormControl<string | null>;
  sv: FormControl<string | null>;
  en: FormControl<string | null>;
  no: FormControl<string | null>;
}

/* 
  Example usage:
  {
    type: "wysiwyg",
    key: "description",
    props: {
      label: {
        en: "Label text",
        fi: "Otsikon teksti",
        sv: "Rubriktext",
      },                                // Optional
      required: true,
      translatable: false,              // Optional: Set to true for multi language mode (default: false)
      description: {
        en: "Some description text",
        fi: "Kuvaileva teksti",
        sv: "Beskrivande text",
      },                                // Optional
    },
  },

  ////////////////////

  JSON example:

  {
    "type": "wysiwyg",
    "key": "description",
    "props": {
      "label": {
        "en": "Label text",
        "fi": "Otsikon teksti",
        "sv": "Rubriktext"
      },                                // Optional
      "required": true,
      "translatable": false,            // Optional: Set to true for multi language mode (default: false)
      "description": {
        "en": "Some description text",
        "fi": "Kuvaileva teksti",
        "sv": "Beskrivande text"
      }                                 // Optional
    }
  },
*/
@Component({
  selector: "app-wysiwyg",
  templateUrl: "./wysiwyg.type.html",
  styleUrls: ["./wysiwyg.type.scss"],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, EditorModule, TranslocoDirective],
})
export class TextEditorComponent extends FieldType<FieldTypeConfig> implements OnInit, OnDestroy {
  readonly languages = langCodes;
  public langFormGroup?: FormGroup;
  public singleFormControl = new FormControl<string | null>("");
  public editorModules: { blotFormatter2: Partial<BlotFormatterOptions> } = {
    blotFormatter2: {
      align: {
        allowAligning: false,
        alignments: [],
      },
      image: {
        allowAltTitleEdit: false,
        linkOptions: {
          allowLinkEdit: false,
          // @ts-expect-error type requires this but links are not enabled
          modal: {},
        },
      },
    },
  };
  private subscription?: Subscription;

  get translatable(): boolean {
    return this.props["translatable"] === true;
  }

  ngOnInit(): void {
    const required = !!this.props.required;
    const validator = required ? [Validators.required, this.htmlNotEmptyValidator()] : [];

    if (this.translatable) {
      const value = this.formControl.value ?? { fi: "", sv: "", en: "", no: "" };
      this.formControl.setValue(value, { emitEvent: false });

      this.langFormGroup = new FormGroup({
        fi: new FormControl(value.fi, validator),
        sv: new FormControl(value.sv, validator),
        en: new FormControl(value.en, validator),
        no: new FormControl(value.no, validator),
      } satisfies LangFormControls);

      // Initial validity sync
      if (this.langFormGroup.invalid) {
        this.formControl.setErrors({ invalid: true });
      }

      this.subscription = this.langFormGroup.valueChanges.subscribe((val) => {
        this.formControl.setValue(val, { emitEvent: false });
        this.formControl.markAsDirty();

        if (this.langFormGroup?.valid) {
          this.formControl.setErrors(null);
        } else {
          this.formControl.setErrors({ invalid: true });
        }
      });
    } else {
      const value = this.formControl.value ?? "";
      this.formControl.setValue(value);
      this.singleFormControl = new FormControl(value, validator);

      this.subscription = this.singleFormControl.valueChanges.subscribe((val) => {
        this.formControl.setValue(val, { emitEvent: false });
        this.formControl.markAsDirty();

        if (this.singleFormControl.valid) {
          this.formControl.setErrors(null);
        } else {
          this.formControl.setErrors({ invalid: true });
        }
      });
    }

    this.handleParentSubmitMarking();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private handleParentSubmitMarking() {
    this.field.options?.parentForm?.statusChanges?.subscribe(() => {
      if (this.field.options?.parentForm?.submitted) {
        if (this.translatable) {
          this.langFormGroup?.markAllAsTouched();
        } else {
          this.singleFormControl.markAsTouched();
        }
      }
    });
  }

  public onBlur(field: keyof LangFormControls | "single"): void {
    if (this.translatable) {
      this.langFormGroup?.get(field)?.markAsTouched();
    } else {
      this.singleFormControl.markAsTouched();
    }
    this.formControl.markAsTouched();
  }

  public isFieldInvalid(field: keyof LangFormControls): boolean {
    const control = this.langFormGroup?.get(field);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  public isSingleFieldInvalid(): boolean {
    return this.singleFormControl.invalid && (this.singleFormControl.touched || this.singleFormControl.dirty);
  }

  private htmlNotEmptyValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const text = (control.value || "")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, "")
        .trim();
      return text === "" ? { required: true } : null;
    };
  }
}
